import { ref, nextTick, watch, onBeforeUnmount } from 'vue'
import { useRoute, RouteLocationNormalizedLoaded } from 'vue-router'
import type { Component } from 'vue'

interface ComponentWithName extends Component {
  name?: string
  __name?: string
}

const caches = ref<string[]>([])
const refreshPages = new Map<string, boolean>()
let collect = false

export default function useRouteCache() {
  const route = useRoute()
  let unwatchRoute: (() => void) | null = null

  function getComponentName(route: RouteLocationNormalizedLoaded): string | undefined {
    const componentDef = route.components?.default as ComponentWithName
    return componentDef?.name || componentDef?.__name
  }

  function collectRouteCaches() {
    route.matched.forEach(routeMatch => {
      const componentName = getComponentName(routeMatch)

      if (routeMatch.meta.keepAlive) {
        if (!componentName) {
          console.warn(`[useRouteCache] ${routeMatch.path} 路由组件缺少 name`)
          return
        }
        addCache(componentName)
      } else {
        removeCache(componentName)
      }
    })
  }

  function collectCaches() {
    if (collect) {
      console.warn('[useRouteCache] 缓存已在收集中')
      return
    }
    collect = true
    unwatchRoute = watch(() => route.path, collectRouteCaches, { immediate: true })
  }

  function addCache(componentName: string | string[]) {
    if (Array.isArray(componentName)) {
      componentName.forEach(addCache)
      return
    }

    if (!componentName || caches.value.includes(componentName)) return
    caches.value.push(componentName)
  }

  function removeCache(componentName: string | string[]) {
    if (Array.isArray(componentName)) {
      componentName.forEach(removeCache)
      return
    }

    const index = caches.value.indexOf(componentName)
    if (index > -1) {
      refreshPages.delete(componentName)
      return caches.value.splice(index, 1)
    }
  }

  async function removeCacheEntry(componentName: string) {
    if (removeCache(componentName)) {
      await nextTick()
      addCache(componentName)
    }
  }

  function addRefreshPage(componentName?: string) {
    const pageName = componentName || (route.meta.from as string) || ''
    if (pageName && !refreshPages.get(pageName)) {
      refreshPages.set(pageName, true)
    }
  }

  function checkIsNeedRefresh(componentName?: string) {
    const pageName = componentName || (route.name as string)
    const refresh = refreshPages.has(pageName)
    refreshPages.delete(pageName)
    return refresh
  }

  function removeAllCache() {
    caches.value = []
    clearRefreshPages()
  }

  function clearRefreshPages() {
    refreshPages.clear()
  }

  function getRefreshPage() {
    return Array.from(refreshPages.keys())
  }

  onBeforeUnmount(() => {
    unwatchRoute?.()
    collect = false
    clearRefreshPages()
  })

  return {
    caches,
    collectCaches,
    addCache,
    removeCache,
    addRefreshPage,
    removeAllCache,
    getRefreshPage,
    removeCacheEntry,
    checkIsNeedRefresh
  }
}
